import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import type { SharedProject } from '../../types/api';
import type { TFunction } from 'i18next';

interface SelectedLinkNode {
  name?: string;
  [key: string]: any;
}

interface SelectedLink {
  source: SelectedLinkNode | string | number;
  target: SelectedLinkNode | string | number;
  [key: string]: any;
}

interface SharedProjectsDialogProps {
  selectedLink: SelectedLink | null;
  sharedProjects: SharedProject[];
  sharedProjectsLoading: boolean;
  onClose: () => void;
  onProjectClick: (projectId: number) => void;
  t: TFunction;
}

export default function SharedProjectsDialog({
  selectedLink,
  sharedProjects,
  sharedProjectsLoading,
  onClose,
  onProjectClick,
  t,
}: SharedProjectsDialogProps) {
  if (!selectedLink) return null;

  const sourceName = typeof selectedLink.source === 'object' ? (selectedLink.source as SelectedLinkNode).name : selectedLink.source;
  const targetName = typeof selectedLink.target === 'object' ? (selectedLink.target as SelectedLinkNode).name : selectedLink.target;

  return (
    <Dialog open={!!selectedLink} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[700px] max-h-[70vh] flex flex-col overflow-hidden p-0">
        <div className="px-6 pt-6 pb-5 border-b border-ctu-border">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-ctu-text">
              {t('collaborationGraph.sharedProjects.title')}
              {!sharedProjectsLoading && (
                <span className="ml-2 text-[11px] font-mono tabular-nums text-ctu-text-muted">
                  {sharedProjects.length}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-ctu-text-muted">
              {t('collaborationGraph.sharedProjects.between')}{' '}
              <span className="text-ctu-text font-medium">{sourceName}</span>
              {' '}{t('collaborationGraph.sharedProjects.and')}{' '}
              <span className="text-ctu-text font-medium">{targetName}</span>
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="overflow-y-auto flex-1">
          {sharedProjectsLoading ? (
            <div className="p-8 text-center text-sm text-ctu-text-muted">
              {t('collaborationGraph.loading')}
            </div>
          ) : sharedProjects.length === 0 ? (
            <div className="p-8 text-center text-sm text-ctu-text-muted">
              {t('collaborationGraph.sharedProjects.noProjects')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-[11px] font-semibold text-ctu-text-muted uppercase tracking-[0.14em]">
                    {t('table.projectName')}
                  </TableHead>
                  <TableHead className="text-[11px] font-semibold text-ctu-text-muted uppercase tracking-[0.14em]">
                    {t('table.type')}
                  </TableHead>
                  <TableHead className="text-[11px] font-semibold text-ctu-text-muted uppercase tracking-[0.14em] text-right">
                    {t('table.year')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sharedProjects.map((proj: SharedProject) => (
                  <TableRow
                    key={proj.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => onProjectClick(proj.id)}
                  >
                    <TableCell className="text-sm py-3 font-medium text-ctu-text hover:text-primary">
                      {proj.name}
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge variant="outline" className="font-normal">
                        {proj.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-ctu-text-muted text-right font-mono tabular-nums py-3">
                      {proj.year}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
